import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useInvalidateBills } from '@features/bills/hooks/useInvalidateBills'
import { useInvalidateInvoices } from '@features/invoices/hooks/useInvalidateInvoices'
import { InvoiceType } from '@features/invoicesLegacy/types/invoiceType'
import { useUserOrganization } from '@modules-deprecated/app/organization'
import { Difference, DifferenceType } from '@modules-deprecated/bankReconciliation/types'
import { useCreateManualPayment } from '@views/bills/hooks/useCreateManualPayment'

import { Side } from '../../../enums/side'
import { useSegment } from '../../../hooks'
import { useMultiplePayableSubjects } from '../../paymentReconciliationModal/contexts'
import { PayableSubjectType } from '../../paymentReconciliationModal/types/payableSubjectType'
import { calculateExchangeRate } from '../../paymentReconciliationModal/utils'
import { PaymentRegistrationSuccessState } from '../enums/paymentRegistrationSuccessState'
import { PayableSubject } from '../types'
import { getSubjectReferences } from '../utils/getSubjectReferencesFromBills'
import { notifyPaymentRegistrationError } from '../utils/notifyPaymentRegistrationError'
import { notifyPaymentRegistrationSuccess } from '../utils/notifyPaymentRegistrationSuccess'

interface UseRegisterManualPaymentProps {
  cashSide?: Side
  crossCurrencyPaymentAmount?: number
  isCrossCurrency?: boolean
  selectedDifferenceType: DifferenceType | undefined
  paymentDate: string | undefined
  selectedAccountId: string
  manualPaymentAmount: number
  manualPaymentDifference: Difference
  payableSubjectType: PayableSubjectType
  onSuccess: () => void
}

export const useRegisterManualPayment = <T extends PayableSubject>({
  cashSide,
  crossCurrencyPaymentAmount,
  isCrossCurrency,
  selectedDifferenceType,
  paymentDate,
  selectedAccountId,
  manualPaymentAmount,
  manualPaymentDifference,
  onSuccess,
  payableSubjectType,
}: UseRegisterManualPaymentProps) => {
  const [isProcessing, setIsProcessing] = useState(false)
  const { t } = useTranslation()
  const { track } = useSegment()
  const { invalidateBills, invalidateBill } = useInvalidateBills()
  const { invalidateInvoice, invalidateInvoices, invalidateExternalInvoice, invalidateExternalInvoices } =
    useInvalidateInvoices()
  const { initialSubject } = useMultiplePayableSubjects()
  const subjectId = initialSubject?.id
  const { create: createManualPayment } = useCreateManualPayment({
    onSuccess: () => {
      payableSubjectType === InvoiceType.Invoice
        ? track('Invoice Payment Created (Client)', {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            invoice_id: subjectId,
            // eslint-disable-next-line @typescript-eslint/naming-convention
            is_auto_matched: false,
            // eslint-disable-next-line @typescript-eslint/naming-convention
            payment_mode: 'manual',
          })
        : // eslint-disable-next-line @typescript-eslint/naming-convention
          track('Bill Payment Created (Client)', { bill_id: subjectId, is_auto_matched: false, payment_mode: 'manual' })
      notifyPaymentRegistrationSuccess(PaymentRegistrationSuccessState.Registered, t)
      invalidateSubjectResources()
      setIsProcessing(false)

      onSuccess()
    },
    onError: () => {
      notifyPaymentRegistrationError(t)
      setIsProcessing(false)
    },
  })

  const { organization } = useUserOrganization()

  const { selectedSubjects } = useMultiplePayableSubjects<T>()

  const subjectReferences = useMemo(
    () => getSubjectReferences(selectedSubjects ?? [], payableSubjectType),
    [selectedSubjects, payableSubjectType],
  )

  const invalidateSubjectResources = useCallback(() => {
    switch (payableSubjectType) {
      case InvoiceType.Invoice:
        invalidateInvoice(subjectId)
        invalidateInvoices()

        break
      case InvoiceType.ExternalInvoice:
        invalidateExternalInvoice(subjectId)
        invalidateExternalInvoices()

        break
      default:
        invalidateBill(subjectId)
        invalidateBills()
    }
  }, [
    invalidateBill,
    invalidateBills,
    invalidateExternalInvoice,
    invalidateExternalInvoices,
    invalidateInvoice,
    invalidateInvoices,
    payableSubjectType,
    subjectId,
  ])

  const registerManualPayment = useCallback(async () => {
    setIsProcessing(true)
    let bankFeePayload
    if (selectedDifferenceType === 'bankFee' && manualPaymentDifference.differenceAmount) {
      bankFeePayload = {
        feeAccountId: organization?.defaultBankFeeAccountId as string,
        feeAmount: Math.abs(manualPaymentDifference.differenceAmount),
      }
    }

    let exchangeRatePayload
    if (isCrossCurrency) {
      exchangeRatePayload = { cashExchangeRate: calculateExchangeRate(manualPaymentAmount, crossCurrencyPaymentAmount) }
    }

    const manualPaymentPayload = {
      associations: subjectReferences,
      cashAccountId: selectedAccountId,
      cashAmount: manualPaymentAmount,
      cashSide: cashSide ?? Side.Credit,
      entryDate: paymentDate as string,
      organizationId: organization?.id as string,
      ...bankFeePayload,
      ...exchangeRatePayload,
    }
    createManualPayment(manualPaymentPayload)
  }, [
    cashSide,
    createManualPayment,
    crossCurrencyPaymentAmount,
    isCrossCurrency,
    manualPaymentAmount,
    manualPaymentDifference.differenceAmount,
    organization?.defaultBankFeeAccountId,
    organization?.id,
    paymentDate,
    selectedAccountId,
    selectedDifferenceType,
    subjectReferences,
  ])

  return {
    registerManualPayment,
    isProcessing,
  }
}
